// <u4-viewer> — Universal 4.0 in 3D: card + pivoting Cut Tool insert, DXF-derived engraving decals, HTML markers.
// Reads tools from window.U4, selection from window.__u4active, reports clicks via window.__u4select(n).
(function () {
  const THREE_URL = 'https://unpkg.com/three@0.184.0/build/three.module.js';
  const W = 85, H = 55, T = 2.4;             // card mm
  const FZ = 1;                              // z-sign of the FRONT (logo) face once the card is stood up
                                             // (verified against the STL: face A only lines up with the real cutouts on this side)
  const nz = (side) => (side === 'A' ? FZ : -FZ);
  const PIVOT = [8.4, -28.6];                // insert hinge in raw STL mm (x, z)
  // a tool's spot on its face, in card mm — a hand-placed mark wins over the authored guess
  function spot(t, mark) {
    if (mark && mark.mx != null) return { mx: mark.mx, my: mark.my, side: mark.side, mine: true };
    const CM = window.CardMap;
    const mm = CM ? CM.mmOf('u4', t) : { mx: t.mx || 0, my: t.my || 0 };
    return { mx: mm.mx, my: mm.my, side: t.side, mine: !t.est };
  }

  function parseSTL(buf) {
    const dv = new DataView(buf);
    const tri = dv.getUint32(80, true);
    if (84 + tri * 50 !== buf.byteLength) throw new Error('only binary STL supported');
    const pos = new Float32Array(tri * 9);
    for (let i = 0; i < tri; i++) {
      const o = 84 + i * 50;
      for (let v = 0; v < 3; v++) {
        const p = o + 12 + v * 12, k = i * 9 + v * 3;
        pos[k] = dv.getFloat32(p, true); pos[k + 1] = dv.getFloat32(p + 4, true); pos[k + 2] = dv.getFloat32(p + 8, true);
      }
    }
    return { pos, tri };
  }

  // split welded solids so the insert can swing independently of the card
  function shells(pos, tri) {
    const key = (i) => Math.round(pos[i * 3] * 2e4) + '_' + Math.round(pos[i * 3 + 1] * 2e4) + '_' + Math.round(pos[i * 3 + 2] * 2e4);
    const map = new Map(), vid = new Int32Array(tri * 3);
    let nv = 0;
    for (let i = 0; i < tri * 3; i++) { const k = key(i); let id = map.get(k); if (id === undefined) { id = nv++; map.set(k, id); } vid[i] = id; }
    const par = new Int32Array(nv); for (let i = 0; i < nv; i++) par[i] = i;
    const find = (a) => { while (par[a] !== a) { par[a] = par[par[a]]; a = par[a]; } return a; };
    const uni = (a, b) => { a = find(a); b = find(b); if (a !== b) par[b] = a; };
    for (let t = 0; t < tri; t++) { uni(vid[t * 3], vid[t * 3 + 1]); uni(vid[t * 3 + 1], vid[t * 3 + 2]); }
    const groups = new Map();
    for (let t = 0; t < tri; t++) { const r = find(vid[t * 3]); if (!groups.has(r)) groups.set(r, []); groups.get(r).push(t); }
    return [...groups.values()].map(tris => {
      const p = new Float32Array(tris.length * 9);
      let bb = [1e9, 1e9, 1e9, -1e9, -1e9, -1e9];
      tris.forEach((t, i) => {
        for (let k = 0; k < 9; k++) p[i * 9 + k] = pos[t * 9 + k];
        for (let v = 0; v < 3; v++) {
          const b = t * 9 + v * 3;
          bb[0] = Math.min(bb[0], pos[b]); bb[3] = Math.max(bb[3], pos[b]);
          bb[1] = Math.min(bb[1], pos[b + 1]); bb[4] = Math.max(bb[4], pos[b + 1]);
          bb[2] = Math.min(bb[2], pos[b + 2]); bb[5] = Math.max(bb[5], pos[b + 2]);
        }
      });
      return { p, n: tris.length, bb };
    }).sort((a, b) => b.n - a.n);
  }

  class U4Viewer extends HTMLElement {
    connectedCallback() {
      if (this._up) { this._revive(); return; }
      this._up = true;
      this.style.cssText = 'position:relative; display:block; width:100%; height:100%; min-height:280px; background:radial-gradient(120% 100% at 50% 42%, #262C33 0%, #1A1E24 58%, #11141A 100%); touch-action:none;';
      this.innerHTML =
        '<canvas style="display:block; width:100%; height:100%; cursor:grab;"></canvas>' +
        '<svg data-lead style="position:absolute; inset:0; width:100%; height:100%; pointer-events:none; overflow:visible;"><line stroke="#FF6B2C" stroke-width="1.5" stroke-dasharray="4 3" opacity="0"></line><circle r="3" fill="#FF6B2C" opacity="0"></circle></svg>' +
        '<div data-labels style="position:absolute; inset:0; pointer-events:none;"></div>' +
        '<div data-hint style="position:absolute; left:12px; bottom:12px; font-family:\'IBM Plex Mono\',monospace; font-size:11px; letter-spacing:0.1em; color:rgba(236,237,238,0.72); background:rgba(11,12,14,0.72); padding:4px 8px;">LOADING 3D…</div>' +
        '<div data-btns style="position:absolute; right:12px; top:12px; display:flex; gap:6px; flex-wrap:wrap; justify-content:flex-end; max-width:calc(100% - 24px);"></div>';
      this.canvas = this.querySelector('canvas');
      this.labels = this.querySelector('[data-labels]');
      this.lead = this.querySelector('[data-lead] line');
      this.leadDot = this.querySelector('[data-lead] circle');
      this.hint = this.querySelector('[data-hint]');
      // a remount leaves a stale RAF id behind, so liveness is judged by heartbeat, never by the id
      this._revive = () => {
        if (!this._tickFn || !this.isConnected) return;
        if (this._beat != null && performance.now() - this._beat < 250) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        this._tickFn();
      };
      this._watchdog = setInterval(() => {
        if (this.isConnected && this.clientWidth > 0 && this._tickFn && (this._beat == null || performance.now() - this._beat > 400)) { this._revive(); if (this.refresh && (this._beat == null || performance.now() - this._beat > 400)) this.refresh(); }
      }, 500);
      this._mkBtn = (label, fn) => {
        const b = document.createElement('button');
        b.textContent = label;
        b.style.cssText = "cursor:pointer; font-family:'IBM Plex Mono',monospace; font-size:11px; letter-spacing:0.06em; color:#ECEDEE; background:rgba(11,12,14,0.78); border:1px solid #33383F; border-radius:9px; padding:7px 10px;";
        b.onmouseenter = () => { b.style.borderColor = '#FF6B2C'; };
        b.onmouseleave = () => { if (!b.dataset.on) b.style.borderColor = '#33383F'; };
        b.onclick = fn;
        this.querySelector('[data-btns]').appendChild(b);
        return b;
      };
      this.boot().catch(e => { this.hint.textContent = '3D FAILED: ' + e.message; console.error('u4-viewer', e); });
    }

    async boot() {
      const THREE = await import(THREE_URL);
      this.THREE = THREE;
      const renderer = new THREE.WebGLRenderer({ canvas: this.canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(Math.min(devicePixelRatio, 1.75));
      const scene = new THREE.Scene();
      const camera = new THREE.PerspectiveCamera(38, 1, 1, 3000);
      scene.add(new THREE.HemisphereLight(0xf2f4f7, 0x4a5059, 0.55));
      const key = new THREE.DirectionalLight(0xffffff, 1.0); key.position.set(70, 130, 150); scene.add(key);
      const rim = new THREE.DirectionalLight(0xcfe0f5, 1.05); rim.position.set(-90, -50, -130); scene.add(rim);
      const fill = new THREE.DirectionalLight(0xffffff, 0.5); fill.position.set(-40, 90, -60); scene.add(fill);
      (() => {   // studio env — metals read as flat black without one
        const c = document.createElement('canvas'); c.width = 512; c.height = 256;
        const x = c.getContext('2d');
        const gr = x.createLinearGradient(0, 0, 0, 256);
        gr.addColorStop(0, '#eef2f7'); gr.addColorStop(0.5, '#9aa3ad'); gr.addColorStop(1, '#2a2e33');
        x.fillStyle = gr; x.fillRect(0, 0, 512, 256);
        x.fillStyle = 'rgba(255,255,255,0.95)'; x.fillRect(60, 30, 150, 60);
        x.fillStyle = 'rgba(210,225,245,0.7)'; x.fillRect(330, 60, 90, 90);
        const tex = new THREE.CanvasTexture(c);
        tex.mapping = THREE.EquirectangularReflectionMapping;
        const pm = new THREE.PMREMGenerator(renderer);
        scene.environment = pm.fromEquirectangular(tex).texture;
        tex.dispose(); pm.dispose();
      })();
      const black = new THREE.MeshStandardMaterial({ name: 'pvd-black', color: 0x17191d, metalness: 0.55, roughness: 0.42, envMapIntensity: 0.7 });
      const ti = new THREE.MeshStandardMaterial({ name: 'titanium', color: 0xc9cfd6, metalness: 0.86, roughness: 0.34, envMapIntensity: 1.4 });
      const orange = new THREE.MeshStandardMaterial({ name: 'blade-cerakote', color: 0xE2551A, metalness: 0.25, roughness: 0.48, envMapIntensity: 0.9, side: THREE.DoubleSide });
      const plate = new THREE.MeshStandardMaterial({ name: 'insert-carrier', color: 0x1b1e22, metalness: 0.4, roughness: 0.5, envMapIntensity: 1, side: THREE.DoubleSide });
      const steel = new THREE.MeshStandardMaterial({ name: 'pivot-steel', color: 0xB8BEC6, metalness: 0.95, roughness: 0.22, envMapIntensity: 1.5 });

      const { pos, tri } = parseSTL(await (await fetch('https://mrf.tools/cdn/shop/files/mrf26-u4-kit.stl?v=b3')).arrayBuffer());
      const parts = shells(pos, tri);
      const span = (bb, i) => bb[i + 3] - bb[i];
      // the card is the wide, thin solid; everything else belongs to the insert
      let cardPart = parts[0], best = 1e9;
      parts.forEach(p => { const err = Math.abs(span(p.bb, 0) * 10 - W) + Math.abs(span(p.bb, 2) * 10 - H); if (err < best) { best = err; cardPart = p; } });
      const scale = W / (span(cardPart.bb, 0) * 1);           // STL units -> mm
      const off = [-(cardPart.bb[0] + cardPart.bb[3]) / 2, -(cardPart.bb[1] + cardPart.bb[4]) / 2, -(cardPart.bb[2] + cardPart.bb[5]) / 2];
      const mkGeo = (part) => {
        const g = new THREE.BufferGeometry();
        g.setAttribute('position', new THREE.BufferAttribute(part.p.slice(), 3));
        g.translate(off[0], off[1], off[2]);
        g.scale(scale, scale, scale);
        g.computeVertexNormals();
        return g;
      };
      const root = new THREE.Group();
      root.rotation.x = -Math.PI / 2;      // stand the card up: thickness lands on Z
      scene.add(root);
      const card = new THREE.Mesh(mkGeo(cardPart), black);
      card.name = 'universal4-card';
      root.add(card);

      // the KIT arrives as one welded solid: the blade is the layer inside the card's own slot,
      // the clamp plates sit above and below it — split by that band so only the blade turns
      // the KIT is one welded solid of three 2.4 mm plates: pliers on one face, the black carrier
      // inside the card's slot, the knife on the other face. Only the knife turns.
      const modPart = parts.filter(p => p !== cardPart).sort((a, b) => b.n - a.n)[0];
      const cy0 = cardPart.bb[1], cy1 = cardPart.bb[4], eps = (cy1 - cy0) * 0.05;
      const lowT = [], coreT = [], highT = [];
      for (let t = 0; t < modPart.n; t++) {
        const a = modPart.p[t * 9 + 1], b = modPart.p[t * 9 + 4], c = modPart.p[t * 9 + 7];
        const lo = Math.min(a, b, c), hi = Math.max(a, b, c);
        if (lo > cy0 - eps && hi < cy1 + eps) coreT.push(t);          // sits in the slot
        else if (hi <= cy0 + eps) lowT.push(t);
        else if (lo >= cy1 - eps) highT.push(t);
        else {                                                        // rivet bosses bridging the plates: keep them with a plate, never with the carrier
          const mid = (lo + hi) / 2;
          (mid < (cy0 + cy1) / 2 ? lowT : highT).push(t);
        }
      }
      // the pliers' serrated jaws carry far more triangles than the plain knife slab
      const knifeT = highT.length <= lowT.length ? highT : lowT;
      const pliersT = knifeT === highT ? lowT : highT;
      const sub = (list) => {
        const f = new Float32Array(list.length * 9);
        list.forEach((t, i) => { for (let k = 0; k < 9; k++) f[i * 9 + k] = modPart.p[t * 9 + k]; });
        return { p: f };
      };
      const px = (PIVOT[0] / 10 + off[0]) * scale, pz = (PIVOT[1] / 10 + off[2]) * scale;
      const kit = new THREE.Group();
      kit.visible = false;
      root.add(kit);
      const hingeP = new THREE.Group();
      hingeP.position.set(px, 0, pz);
      kit.add(hingeP);
      const innerP = new THREE.Group();
      innerP.position.set(-px, 0, -pz);
      hingeP.add(innerP);
      const frame = new THREE.Mesh(mkGeo(sub(pliersT)), orange);
      frame.name = 'pliers';
      innerP.add(frame);
      const core = new THREE.Mesh(mkGeo(sub(coreT)), plate);
      core.name = 'insert-carrier';
      kit.add(core);
      parts.forEach(p => {
        if (p === cardPart || p === modPart) return;
        const m = new THREE.Mesh(mkGeo(p), steel);
        m.name = 'pivot-hardware';
        kit.add(m);
      });
      const hinge = new THREE.Group();
      hinge.position.set(px, 0, pz);
      kit.add(hinge);
      const inner = new THREE.Group();
      inner.position.set(-px, 0, -pz);
      hinge.add(inner);
      const blade = new THREE.Mesh(mkGeo(sub(knifeT)), orange);
      blade.name = 'knife';
      inner.add(blade);
      this.insert = kit; this.hinge = hinge;

      const loader = new THREE.TextureLoader();
      const decal = (file, sign) => {
        const tex = loader.load(file, () => { this._dirty = true; });
        tex.anisotropy = 8;
        // punched by the card's own silhouette: no engraving over a cutout or past an edge
        const m = new THREE.Mesh(new THREE.PlaneGeometry(W, H), new THREE.MeshBasicMaterial({ map: tex, transparent: true, opacity: 0.96, depthWrite: false }));
        m.position.z = sign * (T / 2 + 0.02);
        if (sign < 0) m.rotation.y = Math.PI;
        m.name = 'marking-' + (sign > 0 ? 'a' : 'b');
        scene.add(m);
        return m;
      };
      this.decals = [decal('https://mrf.tools/cdn/shop/files/mrf26-u4-decal-a.png?v=b3', nz('A')), decal('https://mrf.tools/cdn/shop/files/mrf26-u4-decal-b.png?v=b3', nz('B'))];

      const FRONT_T = (FZ > 0 ? 0 : Math.PI) + 0.35;   // open on the FRONT (logo) face, whichever z-sign it sits on
      let theta = FRONT_T, phi = 1.25, r = 175, drag = null, auto = true, home = FRONT_T, sway = 0;
      this._fit = () => {
        const w = this.clientWidth || 1, h = this.clientHeight || 1;
        const vFov = camera.fov * Math.PI / 180;
        const need = Math.max(H / 2 / Math.tan(vFov / 2), W / 2 / Math.tan(vFov / 2) / (w / h));
        this._base = need * 1.3;
        // swung out, the KIT reaches 60 mm past the card — pull back so it stays in frame
        if (!this._zoomed) r = this._base * (this._wide ? 1.95 : 1);
      };
      this._fit();
      const place = () => {
        camera.position.set(r * Math.sin(phi) * Math.sin(theta), r * Math.cos(phi), r * Math.sin(phi) * Math.cos(theta));
        camera.lookAt(0, 0, 0);
      };
      // screen point -> mm on whichever face is turned towards the camera (placement mode)
      this._hit = (cx, cy) => {
        const rect = this.canvas.getBoundingClientRect();
        if (!rect.width || !rect.height || Math.abs(camera.position.z) < 6) return null;
        const side = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        const v3 = new THREE.Vector3(((cx - rect.left) / rect.width) * 2 - 1, -(((cy - rect.top) / rect.height) * 2 - 1), 0.5);
        v3.unproject(camera);
        const dir = v3.sub(camera.position);
        if (Math.abs(dir.z) < 1e-6) return null;
        const k = (nz(side) * (T / 2) - camera.position.z) / dir.z;
        if (k < 0) return null;
        const wx = camera.position.x + dir.x * k, wy = camera.position.y + dir.y * k;
        const mx = nz(side) > 0 ? wx + W / 2 : W / 2 - wx;
        return { side: side, mx: Math.max(0, Math.min(W, mx)), my: Math.max(0, Math.min(H, H / 2 - wy)) };
      };
      this._dropAt = (cx, cy, n, advance) => {
        const h = this._hit(cx, cy);
        if (!h) return;
        // a live drag must not rebuild the label (that would release pointer capture): move this one in place
        const mk = (this.markers || []).find(m => m.n === n);
        if (mk) {
          const s2 = nz(h.side), MW = W, MH = H, MT = T;
          mk.side = h.side;
          mk.p = [s2 > 0 ? h.mx - MW / 2 : MW / 2 - h.mx, MH / 2 - h.my, s2 * (MT / 2 + 0.2)];
          mk.el.dataset.solid = '1';
          this._dirty = true;
        }
        if (window.__u4place) window.__u4place(n, h.side, h.mx, h.my, advance);
      };

      const pts = new Map();
      let pinch = 0;
      const spread = () => { const a = [...pts.values()]; return Math.hypot(a[0].x - a[1].x, a[0].y - a[1].y); };
      this.canvas.addEventListener('pointerdown', (e) => {
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        auto = false; this._touched = true; this._fly = null;
        this._moved = 0; this._multi = pts.size >= 2;
        if (pts.size === 2) { pinch = spread(); drag = null; }
        else { drag = { x: e.clientX, y: e.clientY }; this.canvas.style.cursor = 'grabbing'; }
        try { this.canvas.setPointerCapture(e.pointerId); } catch (err) {}
      });
      this.canvas.addEventListener('pointermove', (e) => {
        if (!pts.has(e.pointerId)) return;
        pts.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (pts.size >= 2) {                       // two fingers zoom, never spin
          this._multi = true;
          const d = spread();
          if (pinch > 0 && d > 0) { r = Math.min(520, Math.max(95, r * (pinch / d))); this._zoomed = true; }
          pinch = d;
          return;
        }
        if (!drag) return;
        this._moved += Math.abs(e.clientX - drag.x) + Math.abs(e.clientY - drag.y);
        theta -= (e.clientX - drag.x) * 0.008;
        phi = Math.min(Math.PI - 0.12, Math.max(0.12, phi - (e.clientY - drag.y) * 0.008));
        drag = { x: e.clientX, y: e.clientY };
      });
      const stop = (e) => {
        pts.delete(e.pointerId);
        if (pts.size < 2) pinch = 0;
        if (pts.size === 1) { const a = [...pts.values()][0]; drag = { x: a.x, y: a.y }; }
        if (pts.size === 0) { drag = null; this.canvas.style.cursor = window.__u4cal ? 'crosshair' : 'grab'; }
      };
      this.canvas.addEventListener('pointerup', (e) => {
        // a tap on the card in placement mode drops the armed marker; a real drag still spins
        const n = window.__u4cal;
        if (n && !this._multi && this._moved < 7 && pts.size <= 1) this._dropAt(e.clientX, e.clientY, n, true);
      });
      this.canvas.addEventListener('pointerup', stop);
      this.canvas.addEventListener('pointercancel', stop);
      // screen-wide pinch (Tool Explorer forwards two-finger gestures from anywhere on the page)
      this.zoomBy = (f) => { this._touched = true; this._zoomed = true; auto = false; this._fly = null; r = Math.min(520, Math.max(95, r * f)); this._dirty = true; };
      this.cancelDrag = () => { drag = null; pts.clear(); pinch = 0; };
      this.canvas.addEventListener('wheel', (e) => { e.preventDefault(); this._touched = true; this._zoomed = true; r = Math.min(520, Math.max(95, r + e.deltaY * 0.25)); }, { passive: false });

      let pt = null, pp = null, pr = null;
      this._dirty = true;
      this.flyTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; this._fly = { theta: home, phi: 1.25 }; auto = false; };
      this._snapTo = (side) => { home = (nz(side) > 0 ? 0 : Math.PI) + 0.35; theta = home; phi = 1.25; };
      this._mkBtn('FRONT', () => this.flyTo('A'));
      this._mkBtn('BACK', () => this.flyTo('B'));

      // the KIT covers the engraving when folded, so the card ships bare and the insert swings in on demand
      let shown = false, target = 0, angle = 0, targetP = 0, angleP = 0;
      kit.visible = false;
      const paintDeploy = () => {
        db.textContent = target > 0.5 ? 'FOLD BLADE ◂' : 'OPEN BLADE ▸';
        db.dataset.on = target > 0.5 ? '1' : '';
        db.style.borderColor = target > 0.5 ? '#FF6B2C' : '#33383F';
        db.style.color = target > 0.5 ? '#FF6B2C' : '#ECEDEE';
        db.style.opacity = shown ? '1' : '0.35';
        pb.textContent = targetP > 0.5 ? 'FOLD PLIERS ◂' : 'OPEN PLIERS ▸';
        pb.dataset.on = targetP > 0.5 ? '1' : '';
        pb.style.borderColor = targetP > 0.5 ? '#FF6B2C' : '#33383F';
        pb.style.color = targetP > 0.5 ? '#FF6B2C' : '#ECEDEE';
        pb.style.opacity = shown ? '1' : '0.35';
        this._dirty = true;   // kit visibility / deploy state changes meshes, not the camera
        this._wide = shown && (target > 0.5 || targetP > 0.5);
        this._fit();
      };
      const ib = this._mkBtn('+ TOOL KIT', () => {
        shown = !shown;
        kit.visible = shown;
        ib.textContent = shown ? '− TOOL KIT' : '+ TOOL KIT';
        ib.style.borderColor = shown ? '#FF6B2C' : '#33383F';
        ib.style.color = shown ? '#FF6B2C' : '#ECEDEE';
        ib.dataset.on = shown ? '1' : '';
        target = shown ? 1 : 0;          // it arrives with the blade swung out, pliers folded
        if (!shown) { angle = 0; targetP = 0; angleP = 0; }
        auto = false;
        paintDeploy();
      });
      const db = this._mkBtn('OPEN BLADE ▸', () => {
        if (!shown) { ib.click(); return; }
        target = target > 0.5 ? 0 : 1;
        auto = false;
        paintDeploy();
      });
      const pb = this._mkBtn('OPEN PLIERS ▸', () => {
        if (!shown) { ib.click(); targetP = 1; paintDeploy(); return; }
        targetP = targetP > 0.5 ? 0 : 1;
        auto = false;
        paintDeploy();
      });
      paintDeploy();
      let finish = 'black';
      const fb = this._mkBtn('NATURAL', () => {
        finish = finish === 'black' ? 'ti' : 'black';
        card.material = finish === 'black' ? black : ti;
        this.decals.forEach(d => d.material.color.set(finish === 'black' ? 0xffffff : 0x17191c));
        fb.textContent = finish === 'black' ? 'NATURAL' : 'BLACK';
        this._dirty = true;
      });

      // only FRONT/BACK stay on the stage; every other control moves to an in-flow bar above it (hub #finishRow),
      // same pattern as the Designer/Professional colour strips — overlays were covering the model on phones
      (() => {
        const host = document.getElementById('finishRow');
        if (!host) return;
        const bar = document.createElement('div');
        bar.setAttribute('data-finish-for', this.id || 'viewer');
        bar.setAttribute('data-actions-bar', '1');
        bar.style.cssText = 'display:flex; align-items:center; gap:8px; flex-wrap:wrap; border:1px solid #262A30; border-radius:12px; padding:8px 12px; background:#121417;';
        const lab = document.createElement('span');
        lab.textContent = 'FINISH & TOOL KIT';
        lab.style.cssText = "font-family:'IBM Plex Mono',monospace; font-size:10px; letter-spacing:0.18em; color:#FF6B2C; margin-right:4px;";
        bar.appendChild(lab);
        [fb, ib, db, pb].forEach(b => { if (!b) return; b.style.background = 'transparent'; bar.appendChild(b); });
        host.appendChild(bar);
      })();
      this.hint.textContent = 'DRAG TO SPIN · PINCH OR SCROLL TO ZOOM · TAP A NUMBER';
      this.buildLabels();

      const v = new THREE.Vector3(), nrm = new THREE.Vector3(), toCam = new THREE.Vector3();
      // markers move on the compositor (transform), and only when their rounded position actually changed —
      // left/top on 57 chips every frame was relayouting the whole overlay 60x a second
      const put = (mk, x, y, op) => {
        x = Math.round(x * 10) / 10; y = Math.round(y * 10) / 10; op = Math.round(op * 100) / 100;
        if (mk.tx === x && mk.ty === y && mk.op === op) return;
        mk.tx = x; mk.ty = y; mk.op = op;
        mk.el.style.transform = 'translate3d(' + x + 'px,' + y + 'px,0) translate(-50%,-50%)';
        mk.el.style.opacity = String(op);
      };
      const tick = () => {
        if (!this.isConnected) { this._raf = null; return; }
        this._beat = performance.now();
        this._raf = requestAnimationFrame(tick);
        const w = this.clientWidth, h = this.clientHeight;
        if (!w || !h || this._vis === false) return;
        if (this.canvas.width !== Math.round(w * renderer.getPixelRatio()) || this.canvas.height !== Math.round(h * renderer.getPixelRatio())) {
          renderer.setSize(w, h, false);
          camera.aspect = w / h; camera.updateProjectionMatrix();
          this._dirty = true;
          if (!this._touched) this._fit();
        }
        if (auto && !window.__u4cal) {          // idle life, but bounded: the selected face never turns away
          sway += 0.0075;
          const amp = 0.3 * Math.max(0, 1 - sway / 5.4);   // greeting sway decays to rest (~12 s), then the loop costs nothing
          theta = (home == null ? 0.35 : home) + Math.sin(sway) * amp;
          if (amp === 0) { auto = false; theta = home == null ? 0.35 : home; }
        }
        const calOn = !!window.__u4cal;
        if (calOn !== this._calWas) {
          this._calWas = calOn;
          this.canvas.style.cursor = calOn ? 'crosshair' : 'grab';
          this.hint.textContent = calOn ? 'PLACEMENT · TAP THE CARD TO SET THE ARMED NUMBER · DRAG A MARKER TO NUDGE' : 'DRAG TO SPIN · PINCH OR SCROLL TO ZOOM · TAP A NUMBER';
          this.buildLabels();
        }
        if (Math.abs(target - angle) > 0.001) {
          this._dirty = true;
          angle += (target - angle) * 0.075;
          hinge.rotation.y = angle * Math.PI;
        }
        if (Math.abs(targetP - angleP) > 0.001) {
          this._dirty = true;
          angleP += (targetP - angleP) * 0.075;
          hingeP.rotation.y = angleP * Math.PI;   // pliers sweep through the bottom, same arc as the blade
        }
        if (this._fly) {
          let dt = this._fly.theta - theta;
          while (dt > Math.PI) dt -= 2 * Math.PI;
          while (dt < -Math.PI) dt += 2 * Math.PI;
          theta += dt * 0.12; phi += (this._fly.phi - phi) * 0.12;
          if (Math.abs(dt) < 0.01 && Math.abs(this._fly.phi - phi) < 0.01) this._fly = null;
        }
        if (window.__u4block !== this._blk || window.__u4stamp !== this._stamp) this.buildLabels();
        const actNow = window.__u4active;
        if (actNow !== this._act) { this._act = actNow; this.paintActive(); }
        if (!this._dirty && pt !== null && Math.abs(theta - pt) < 1e-4 && Math.abs(phi - pp) < 1e-4 && Math.abs(r - pr) < 1e-4) return;
        this._dirty = false; pt = theta; pp = phi; pr = r;
        place();
        this._faceNow = (camera.position.z > 0) === (FZ > 0) ? 'A' : 'B';
        renderer.render(scene, camera);
        let leadOn = false;
        this.markers.forEach((mk) => {
          v.set(mk.p[0], mk.p[1], mk.p[2]);
          nrm.set(0, 0, nz(mk.side));
          toCam.copy(camera.position).sub(v).normalize();
          const facing = nrm.dot(toCam);
          v.project(camera);
          const sx = (v.x * 0.5 + 0.5) * w, sy = (-v.y * 0.5 + 0.5) * h;
          const on = mk.n === this._act;
          const front = facing > 0.12 && v.z < 1;
          if (!front && !on) { if (mk.vis !== 0) { mk.vis = 0; mk.el.style.display = 'none'; } return; }
          if (mk.vis !== 1) { mk.vis = 1; mk.el.style.display = 'flex'; }
          if (front) {
            put(mk, sx, sy, Math.min(1, 0.35 + facing) * (mk.dim && !on ? 0.55 : 1) * (mk.off && !on ? 0.4 : 1));
            return;
          }
          // selected point faces away: hold it as a callout tied to its anchor
          const dx = sx - w / 2, dy = sy - h / 2, len = Math.hypot(dx, dy) || 1;
          const lx = Math.max(18, Math.min(w - 18, sx + dx / len * 46)), ly = Math.max(18, Math.min(h - 18, sy + dy / len * 46));
          put(mk, lx, ly, 1);
          this.lead.setAttribute('x1', sx); this.lead.setAttribute('y1', sy);
          this.lead.setAttribute('x2', lx); this.lead.setAttribute('y2', ly);
          this.leadDot.setAttribute('cx', sx); this.leadDot.setAttribute('cy', sy);
          leadOn = true;
        });
        const lop = leadOn ? '0.9' : '0';
        if (this._lop !== lop) { this._lop = lop; this.lead.setAttribute('opacity', lop); this.leadDot.setAttribute('opacity', lop); }
      };
      this._tickFn = tick;
      if (window.IntersectionObserver && !this._io) {   // scrolled off the long hub page: stop rendering
        this._io = new IntersectionObserver((es) => {
          const vis = es.some(e => e.isIntersecting);
          if (vis && this._vis === false) { this._dirty = true; this._revive(); }
          this._vis = vis;
        }, { rootMargin: '240px' });
        this._io.observe(this);
      }
      // a mode switch (placement on/off, a new pick) must not wait on the frame loop: the DC calls
      // refresh() and one tick runs synchronously, so cursor, hint, markers and the frame all update
      // even while requestAnimationFrame is throttled (hidden tab, background iframe)
      this.refresh = () => {
        if (!this.isConnected || !this._tickFn) return;
        if (this._raf != null) cancelAnimationFrame(this._raf);
        this._raf = null;
        tick();
      };
      tick();
    }

    buildLabels() {
      if (this._dragN != null) { this.paintActive(); return; }   // never swap the element under a live drag
      const D = window.U4 || { blocks: [] };
      const marks = window.__u4marks || {};
      this.markers = [];
      this.labels.innerHTML = '';
      this._blk = window.__u4block;
      this._stamp = window.__u4stamp;
      // ALWAYS every marker: showing only the open block made switching tabs look like the marks moved.
      // The open block reads at full strength, the rest sit back as context.
      const cal = !!window.__u4cal;
      D.blocks.forEach((b, bi) => b.tools.forEach(t => {
        const off = this._blk != null && bi !== this._blk;
        const f = spot(t, marks[t.n]);
        const solid = f.mine || t.est === false;
        const dim = !solid;                                   // an un-placed guess still shows — just lighter, so it reads as a guess
        const n = nz(f.side);
        const p = [n > 0 ? f.mx - W / 2 : W / 2 - f.mx, H / 2 - f.my, n * (T / 2 + 0.2)];
        const el = document.createElement('button');
        el.textContent = String(t.n).padStart(2, '0');
        el.title = t.name;
        el.style.cssText = "position:absolute; left:0; top:0; opacity:0; will-change:transform; pointer-events:auto; cursor:" + (cal ? 'grab' : 'pointer') + "; width:" + (off ? 17 : 26) + "px; height:" + (off ? 17 : 26) + "px; padding:0; border-radius:50%; font-family:'IBM Plex Mono',monospace; font-size:" + (off ? 8 : 10.5) + "px; font-weight:600; display:flex; align-items:center; justify-content:center; border:1.5px " + (solid ? 'solid' : 'dashed') + " rgba(236,237,238,0.8); background:rgba(11,12,14,0.66); color:#ECEDEE; touch-action:none;";
        el.dataset.solid = solid ? '1' : '';
        el.onclick = () => { if (!cal && window.__u4select) window.__u4select(t.n); };
        if (cal) {
          el.onpointerdown = (e) => {
            e.stopPropagation(); e.preventDefault();
            try { el.setPointerCapture(e.pointerId); } catch (err) {}
            this._dragN = t.n;
            if (window.__u4select) window.__u4select(t.n);
          };
          el.onpointermove = (e) => { if (this._dragN === t.n) this._dropAt(e.clientX, e.clientY, t.n, false); };
          el.onpointerup = (e) => { e.stopPropagation(); this._dragN = null; };
          el.onpointercancel = () => { this._dragN = null; };
        }
        this.labels.appendChild(el);
        this.markers.push({ n: t.n, side: f.side, p, el, dim, off });
      }));
      this._dirty = true;
      this.paintActive();
    }

    paintActive() {
      this._dirty = true;
      const act = window.__u4active;
      (this.markers || []).forEach(mk => {
        const on = mk.n === act;
        mk.el.style.borderStyle = mk.el.dataset.solid || on ? 'solid' : 'dashed';
        mk.el.style.borderColor = on ? '#FF6B2C' : 'rgba(236,237,238,0.8)';
        mk.el.style.background = on ? '#FF6B2C' : 'rgba(11,12,14,0.66)';
        mk.el.style.color = on ? '#0B0C0E' : '#ECEDEE';
        mk.el.style.zIndex = on ? '5' : (mk.off ? '1' : '2');   // context never sits on top of the open block
        mk.el.style.width = mk.el.style.height = on ? '30px' : (mk.off ? '17px' : '26px');
      });
      this._act = act;
      const a = (this.markers || []).find(m => m.n === act);
      if (!a || this._dragN != null) return;
      if (this._lastAct === undefined) { this._lastAct = act; if (this._snapTo) this._snapTo(a.side); return; }
      // a NEW pick turns the card to that face; spinning it by hand afterwards keeps the callout.
      // while placing markers, a pick on the face already in view leaves the camera alone.
      if (this._lastAct !== act) {
        this._lastAct = act;
        if (window.__u4cal && this._faceNow === a.side) return;
        if (this.flyTo) this.flyTo(a.side);
      }
    }

    disconnectedCallback() { if (this._raf != null) cancelAnimationFrame(this._raf); this._raf = null; clearInterval(this._watchdog); if (this._io) { this._io.disconnect(); this._io = null; } }
  }
  if (!window.customElements.get('u4-viewer')) window.customElements.define('u4-viewer', U4Viewer);
})();
